import React, { useState, useEffect } from 'react';
import { Phone, MapPin, Clock, Star, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const LindaLashesWebsite = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const phoneNumber = '0421802327';
  const telLink = `tel:${phoneNumber}`;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <h1 className="text-2xl md:text-3xl font-bold text-rose-900">Linda Lashes Perth</h1>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-rose-900 transition-colors">Services</button>
              <button onClick={() => scrollToSection('reviews')} className="text-gray-700 hover:text-rose-900 transition-colors">Reviews</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-rose-900 transition-colors">Contact</button>
              <a href={telLink} className="flex items-center space-x-2 text-rose-900 font-semibold hover:text-rose-700 transition-colors">
                <Phone className="w-5 h-5" />
                <span>0421 802 327</span>
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-700"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 space-y-3">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 text-gray-700 hover:text-rose-900 transition-colors">Services</button>
              <button onClick={() => scrollToSection('reviews')} className="block w-full text-left py-2 text-gray-700 hover:text-rose-900 transition-colors">Reviews</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 text-gray-700 hover:text-rose-900 transition-colors">Contact</button>
              <a href={telLink} className="flex items-center space-x-2 py-2 text-rose-900 font-semibold">
                <Phone className="w-5 h-5" />
                <span>0421 802 327</span>
              </a>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <span className="text-gray-700 font-medium">5.0 (62 reviews)</span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Flawless Lashes.<br />
                Confidence Boosted.
              </h2>
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                Experience premium eyelash extensions in Perth's most relaxing salon. Personalised service, gentle application, and stunning results that last.
              </p>
              <div className="space-y-4">
                <a href={telLink} className="inline-block">
                  <Button size="lg" className="bg-rose-900 hover:bg-rose-800 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105">
                    <Phone className="w-5 h-5 mr-2" />
                    Call to Book Now
                  </Button>
                </a>
                <p className="text-gray-600 text-sm">Women-owned business • Mount Lawley</p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://lh3.googleusercontent.com/p/AF1QipMycFuyDrB-qky2BHcr6gs6bJTSxoj_5wwT2heH=s338-k-no"
                alt="Linda Lashes Perth salon interior"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-24 bg-rose-50/30 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Services</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Premium lash treatments tailored to enhance your natural beauty
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
              <CardContent className="p-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Eyelash Extensions</h4>
                <p className="text-gray-600 leading-relaxed">
                  Custom-designed lash extensions that complement your eye shape and personal style. Long-lasting, comfortable, and beautifully natural.
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
              <CardContent className="p-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Lash Lifts</h4>
                <p className="text-gray-600 leading-relaxed">
                  Enhance your natural lashes with a professional lift and tint. Wake up with perfectly curled lashes every day.
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
              <CardContent className="p-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Lash Treatments</h4>
                <p className="text-gray-600 leading-relaxed">
                  Nourishing treatments to keep your lashes healthy, strong, and looking their absolute best between appointments.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://lh3.googleusercontent.com/p/AF1QipP8Xl8OsbH1jDe6imv02-Walo600iLgLgCyg7cn=s338-k-no"
                alt="Linda Lashes Perth lash extension close-up"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why Choose Linda Lashes Perth</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="text-xl font-bold text-rose-900 mb-2">Expert Lash Artist</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Linda brings years of experience and genuine passion to every appointment, ensuring you receive the highest quality service.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-rose-900 mb-2">Premium Products</h4>
                  <p className="text-gray-600 leading-relaxed">
                    We use only the finest, salon-grade products that are gentle on your natural lashes and deliver long-lasting results.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-rose-900 mb-2">Relaxing Experience</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Our peaceful salon environment is designed to help you unwind. Many clients fall asleep during their treatment!
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-rose-900 mb-2">Personalised Service</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Every client receives a customised consultation to create the perfect lash style for your unique features and lifestyle.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-16 md:py-24 bg-rose-50/30 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h3>
            <div className="flex items-center justify-center space-x-2 mb-2">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-6 h-6 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-xl font-bold text-gray-900">5.0</span>
            </div>
            <p className="text-gray-600">Based on 62 Google reviews</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  "Absolutely love my lashes! Linda was so lovely and accommodating from start to finish, she really took the time to make sure I was comfortable and happy with the look. The salon itself is super relaxing and peaceful."
                </p>
                <p className="text-sm font-semibold text-gray-900">Hen Si</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  "Linda was amazing. I usually do not like getting my lashes done, and feel uncomfortable but she made me feel so comfortable that I fell asleep for the first time while getting my lashes done. Highly recommend her."
                </p>
                <p className="text-sm font-semibold text-gray-900">Priya Krishnan</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  "There aren't many out there like her. She listens, she matches the style to your eyes which I think is a huge part of extensions and she is such a happy beautiful person. I was so comfortable throughout the appointment!"
                </p>
                <p className="text-sm font-semibold text-gray-900">Tee Bond</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Location & Hours Section */}
      <section id="contact" className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Visit Us</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 text-rose-900 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Location</h4>
                    <p className="text-gray-600">c8/83 Walcott St<br />Mount Lawley WA 6050</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Clock className="w-6 h-6 text-rose-900 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Opening Hours</h4>
                    <div className="space-y-2 text-gray-600">
                      <div className="flex justify-between">
                        <span>Monday - Thursday</span>
                        <span className="font-medium">9am - 7pm</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Friday - Saturday</span>
                        <span className="font-medium">9am - 6pm</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Sunday</span>
                        <span className="font-medium text-rose-900">Closed</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Phone className="w-6 h-6 text-rose-900 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Phone</h4>
                    <a href={telLink} className="text-lg text-rose-900 font-semibold hover:text-rose-700 transition-colors">
                      0421 802 327
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="bg-gray-200 rounded-2xl overflow-hidden shadow-lg h-full min-h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3384.8926848766676!2d115.87145631533743!3d-31.93642138126743!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2a32bad5e2f0e96f%3A0x4b1f7efc5a0f5e7e!2s83%20Walcott%20St%2C%20Mount%20Lawley%20WA%206050!5e0!3m2!1sen!2sau!4v1234567890123!5m2!1sen!2sau"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '400px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Linda Lashes Perth Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-rose-50 to-pink-50 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h3 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            Ready for Beautiful Lashes?
          </h3>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Book your appointment today and experience Perth's best lash service. Quick and easy phone booking.
          </p>
          <a href={telLink} className="inline-block">
            <Button size="lg" className="bg-rose-900 hover:bg-rose-800 text-white px-12 py-7 text-xl rounded-full shadow-xl hover:shadow-2xl transition-all transform hover:scale-105">
              <Phone className="w-6 h-6 mr-3" />
              Call 0421 802 327 Now
            </Button>
          </a>
          <p className="text-gray-600 mt-6">Phone bookings only • Easy and convenient</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-2xl font-bold text-rose-400 mb-4">Linda Lashes Perth</h4>
              <p className="text-gray-300 mb-2">Premium eyelash extensions</p>
              <p className="text-gray-400 text-sm">Women-owned business</p>
            </div>
            <div>
              <h5 className="font-bold mb-4">Contact</h5>
              <div className="space-y-2 text-gray-300">
                <a href={telLink} className="flex items-center space-x-2 hover:text-rose-400 transition-colors">
                  <Phone className="w-4 h-4" />
                  <span>0421 802 327</span>
                </a>
                <div className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                  <span className="text-sm">c8/83 Walcott St, Mount Lawley WA 6050</span>
                </div>
              </div>
            </div>
            <div>
              <h5 className="font-bold mb-4">Hours</h5>
              <div className="space-y-1 text-sm text-gray-300">
                <p>Mon-Thu: 9am - 7pm</p>
                <p>Fri-Sat: 9am - 6pm</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} Linda Lashes Perth. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Sticky Mobile Call Button */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white shadow-2xl border-t border-gray-200 z-40">
        <a href={telLink} className="block">
          <Button size="lg" className="w-full bg-rose-900 hover:bg-rose-800 text-white py-6 text-lg rounded-full shadow-lg">
            <Phone className="w-5 h-5 mr-2" />
            Call to Book - 0421 802 327
          </Button>
        </a>
      </div>
    </div>
  );
};

export default LindaLashesWebsite;